import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ContentCard from '../components/ContentCard';
import PageShell from '../components/PageShell';
import PortfolioCategoryFilter, { getPortfolioFilterKey } from '../components/PortfolioCategoryFilter';
import useSiteContent from '../hooks/useSiteContent';
import { fallbackPortfolio } from '../data/fallbackContent';
import { rooQrPortfolioItem } from '../data/rooQr';
import { velocityRebelPortfolioItem } from '../data/velocityRebel';
import { normalizePortfolioItems } from '../lib/portfolioCategories';
import { syncRecordsByUrl } from '../services/siteContentService';
import '../portfolioCardFix.css';
import '../portfolioFilters.css';

const { FiLayers, FiSearch } = FiIcons;

const legacyDemoTitles = new Set([
  'Generative Futures',
  'Synthetic Sessions',
  'Digital Objects'
]);

const normalizeUrl = (url) => String(url || '').trim().replace(/\/$/, '');

const fallbackCatalog = [
  rooQrPortfolioItem,
  velocityRebelPortfolioItem,
  ...fallbackPortfolio
];
const normalizedFallbackPortfolio = normalizePortfolioItems(fallbackCatalog);

function isFeatured(item) {
  return String(item.featured || '').toUpperCase() === 'TRUE';
}

function sortPortfolioItems(items) {
  return [...items].sort((a, b) => {
    if (isFeatured(a) !== isFeatured(b)) {
      return isFeatured(a) ? -1 : 1;
    }
    return Number(a.order || 0) - Number(b.order || 0);
  });
}

function mergePortfolioItems(items) {
  const normalizedItems = normalizePortfolioItems(items);
  const visibleItems = normalizedItems.filter(
    (item) => !legacyDemoTitles.has(item.title)
  );
  const catalogUrls = new Set(
    normalizedFallbackPortfolio.map((item) => normalizeUrl(item.url))
  );
  const sheetItems = visibleItems.filter(
    (item) => !catalogUrls.has(normalizeUrl(item.url))
  );
  return sortPortfolioItems([...normalizedFallbackPortfolio, ...sheetItems]);
}

export default function Portfolio() {
  const { data: items, loading, refresh } = useSiteContent('portfolio', fallbackCatalog);
  const [query, setQuery] = useState('');
  const [selectedCategories, setSelectedCategories] = useState(() => new Set());
  const databaseSynced = useRef(false);

  const updateSelectedCategories = useCallback((next) => {
    setSelectedCategories(typeof next === 'function' ? next : new Set(next));
  }, []);

  const portfolioItems = useMemo(() => mergePortfolioItems(items), [items]);

  useEffect(() => {
    if (loading || databaseSynced.current) return;
    databaseSynced.current = true;
    syncRecordsByUrl('portfolio', portfolioItems)
      .then(() => refresh())
      .catch(() => {
        databaseSynced.current = false;
      });
  }, [loading, portfolioItems, refresh]);

  const filtered = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    const matches = portfolioItems.filter((item) => {
      const matchesCategory =
        selectedCategories.size === 0 ||
        selectedCategories.has(getPortfolioFilterKey(item));
      const searchable = [
        item.title,
        item.description,
        item.category,
        item.subcategory,
        item.type
      ].join(' ').toLowerCase();
      return matchesCategory && searchable.includes(normalizedQuery);
    });
    return sortPortfolioItems(matches);
  }, [portfolioItems, query, selectedCategories]);

  return (
    <PageShell>
      <section className="page-heading">
        <span className="page-icon">
          <SafeIcon icon={FiLayers} />
        </span>
        <div>
          <p className="eyebrow">Selected work</p>
          <h1>Things I have built</h1>
          <p>Websites, channels, tools, games and creative experiments.</p>
        </div>
      </section>
      <PortfolioCategoryFilter
        items={portfolioItems}
        selected={selectedCategories}
        onChange={updateSelectedCategories}
      />
      <label className="search-box portfolio-search">
        <SafeIcon icon={FiSearch} />
        <span className="sr-only">Search portfolio</span>
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search projects"
        />
      </label>
      <section className="link-stack" aria-label="Portfolio projects">
        {filtered.length ? (
          filtered.map((item, index) => (
            <ContentCard
              key={item.id || item.url}
              item={item}
              entity="portfolio"
              featured={isFeatured(item)}
              index={index}
            />
          ))
        ) : (
          <div className="empty-state">
            No projects match this category or search.
          </div>
        )}
      </section>
    </PageShell>
  );
}